import { gql } from '@apollo/client';
import React from 'react';
import { Button, Checkbox, Col, Row, Space, Tag } from 'antd';

import Table from 'components/Table';
import StatusTag from 'components/StatusTag';
import { useMutation } from 'hooks/apollo';
import { dateFormat } from 'shared/utils/formatter';
import AutoTagComments from './AutoTagComments';

const autoTaggingsColumns = [
  {
    title: 'Date',
    dataIndex: 'createdAt',
    type: 'date',
    defaultSortOrder: 'descend',
  },
  {
    title: 'Created by',
    dataIndex: 'createdBy',
  },
  {
    title: 'Tagged responses',
    dataIndex: 'autoTags',
    type: 'number',
  },
  {
    title: 'Status',
    dataIndex: 'status',
    render: (status) => <StatusTag status={status} />,
  },

];

const REMOVE_AUTO_TAGGING = gql`
  mutation($id: ID!) {
    removeAutoTagging(id: $id)
  }
`;

const TagCommentsAutoTab = ({
  questionsLoading,
  questions,
  onAutoTagDone,
  themes,
  sentiments,
  questionResponseIds,
  questionIds,
  refetchQuestions,
}) => {
  const [removeAutoTagging, { loading: removing }] = useMutation(REMOVE_AUTO_TAGGING);

  const allAutoTaggings = questions?.flatMap((questionData) => questionData.autoTaggings || []) || [];
  const ongoingAutoTagging = allAutoTaggings.find((autoTagging) => autoTagging.status === 'ONGOING');
  const ongoingInput = ongoingAutoTagging?.input || {};
  const ongoingThemeTags = themes.filter((theme) => ongoingInput.tagIds?.includes(theme.id));
  const hasOngoingTagSelection = ongoingThemeTags.length > 0;
  const hasSentimentAnalysis = Boolean(ongoingInput.sentimentIds?.length);
  const hasTranslation = Boolean(ongoingInput.translate);

  const autoTaggingsRows = allAutoTaggings
    .filter((autoTagging) => autoTagging.status !== 'ONGOING')
    .map((autoTagging) => ({
      id: autoTagging.id,
      createdAt: autoTagging.createdAt,
      status: autoTagging.status,
      autoTags: autoTagging.autoTags?.length || 0,
      createdBy: autoTagging.createdBy?.email,
    }));

  const handleRemoveOngoing = async () => {
    if (!ongoingAutoTagging?.id) {
      return;
    }

    await removeAutoTagging({ variables: { id: ongoingAutoTagging.id } });
    refetchQuestions();
  };

  return (
    <Row gutter={[16, 16]}>
      <Col span={12}>
        <h3>Configure</h3>
        <AutoTagComments
          onDone={onAutoTagDone}
          tags={themes}
          sentiments={sentiments}
          questionResponseIds={questionResponseIds}
          questionIds={questionIds}
          hasOngoingAutoTagging={Boolean(ongoingAutoTagging)}
        />
      </Col>
      <Col span={12}>
        <h3>
          Ongoing AI tagging
          {ongoingAutoTagging && <Button danger onClick={handleRemoveOngoing} loading={removing} style={{ float: 'right' }}>Remove</Button>}
        </h3>
        {ongoingAutoTagging ? (
          <div style={{ marginBottom: 16 }}>
            <div>Tags applied to new responses</div>
            {hasOngoingTagSelection ? (
              <div style={{ marginTop: 8 }}>
                <Space size={[8, 8]} wrap>
                  {ongoingThemeTags.map((tag) => <Tag key={tag.id}>{tag.text}</Tag>)}
                </Space>
              </div>
            ) : (
              <div style={{ marginTop: 8 }}>
                <em>No specific tags configured.</em>
              </div>
            )}
            <Space direction="vertical" size={4} style={{ marginTop: 12 }}>
              <Checkbox checked={hasSentimentAnalysis} disabled>
                Sentiment analysis
              </Checkbox>
              <Checkbox checked={hasTranslation} disabled>
                Translate to English
              </Checkbox>
            </Space>
            <br />
            <small>{`Created by ${ongoingAutoTagging.createdBy?.email || '-'} on ${dateFormat(ongoingAutoTagging.createdAt)}`}</small>
          </div>
        ) : (
          <p><em>Ongoing AI tagging not configured, create one to tag new responses automatically.</em></p>
        )}
        <h3>History</h3>
        <Table
          size="small"
          bordered
          loading={questionsLoading}
          columns={autoTaggingsColumns}
          dataSource={autoTaggingsRows}
        />
      </Col>
    </Row>
  );
};

export default TagCommentsAutoTab;
